import React, { useState, useCallback } from 'react';
import LiveTrackingContext from './LiveTrackingContext';
import { liveTrackingService } from '../../services/tracking/liveTrackingService';
import { getCachedVouchers, peekCachedVouchers } from '../../services/vouchers/vouchersCache';

const LiveTrackingState = (props) => {
  const [liveVouchers, setLiveVouchers] = useState([]);
  const [loading, setLoading] = useState(!peekCachedVouchers());
  const [guestsData, setGuestsData] = useState({});
  const [loadingGuests, setLoadingGuests] = useState({});
  const [activeLocationByVoucher, setActiveLocationByVoucher] = useState({});

  const processLiveVouchers = useCallback((vouchersArray) => {
    const arr = Array.isArray(vouchersArray) ? vouchersArray : vouchersArray?.vouchers || [];
    const initialActive = {};

    const mapped = arr.map((v) => {
      const count = Number(v.active_location_count || v.activeLocationCount || (v.active_guests?.length) || 0);
      if (count > 0) {
        initialActive[v._id] = {
          activeCount: count,
          lastUpdated: v.updatedAt || new Date().toISOString()
        };
        if (v.booking_id) {
          initialActive[v.booking_id] = {
            activeCount: count,
            lastUpdated: v.updatedAt || new Date().toISOString()
          };
        }
      }

      return {
        _id: v._id,
        clientName: v.guest_information?.guest_name || '',
        bookingID: v.booking_id,
        destination: v.tour_info?.destinations?.[0]?.city || 'N/A',
        status: v.status,
        updatedAt: v.updatedAt,
        shareToken: v.location_sharing?.share_token || '',
        activeGuests: v.active_guests || [],
        activeLocationCount: count
      };
    });

    setLiveVouchers(mapped);
    setActiveLocationByVoucher((prev) => ({
      ...initialActive,
      ...prev
    }));
  }, []);

  const updateVoucherLiveLocation = useCallback((voucherId, { activeCount, lastUpdated, isActive, bookingId }) => {
    if (!voucherId && !bookingId) return;
    setActiveLocationByVoucher((prev) => {
      const next = { ...prev };
      const count = typeof activeCount === 'number' ? activeCount : (isActive ? 1 : 0);
      const isSharing = isActive !== false && count > 0;

      if (!isSharing) {
        if (voucherId) delete next[voucherId];
        if (bookingId) delete next[bookingId];
      } else {
        const item = {
          activeCount: count > 0 ? count : 1,
          lastUpdated: lastUpdated || new Date().toISOString()
        };
        if (voucherId) next[voucherId] = item;
        if (bookingId) next[bookingId] = item;
      }
      return next;
    });
  }, []);

  const fetchLiveVouchers = useCallback(async (forceRefresh = false, silent = false) => {
    try {
      if (!silent) {
        const cached = peekCachedVouchers();
        if (!forceRefresh && cached) {
          processLiveVouchers(cached);
          setLoading(false);
        } else {
          setLoading(true);
        }
      }
      const data = await getCachedVouchers(forceRefresh);
      processLiveVouchers(data);
    } catch (err) {
      console.error('Error fetching live vouchers:', err);
      throw err;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [processLiveVouchers]);

  const fetchGuests = useCallback(async (voucherId, bookingId) => {
    setLoadingGuests((prev) => ({ ...prev, [voucherId]: true }));
    try {
      let data;
      try {
        data = await liveTrackingService.getGuests(voucherId);
      } catch (err) {
        data = await liveTrackingService.getGuests(bookingId);
      }
      setGuestsData((prev) => ({ ...prev, [voucherId]: data }));
      return data;
    } catch (err) {
      console.error('Error fetching guests:', err);
      throw err;
    } finally {
      setLoadingGuests((prev) => ({ ...prev, [voucherId]: false }));
    }
  }, []);

  const pingGuest = useCallback(async ({ bookingId, guestName = 'all', message }) => {
    return await liveTrackingService.pingGuest({
      bookingId,
      guestName,
      message: message || 'Agent has requested for location. Please start sharing your live location.'
    });
  }, []);

  return (
    <LiveTrackingContext.Provider
      value={{
        liveVouchers,
        loading,
        fetchLiveVouchers,
        guestsData,
        loadingGuests,
        fetchGuests,
        pingGuest,
        activeLocationByVoucher,
        updateVoucherLiveLocation,
        setActiveLocationByVoucher
      }}
    >
      {props.children}
    </LiveTrackingContext.Provider>
  );
};

export default LiveTrackingState;
